import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Settings, 
  Key, 
  Radio,
  PanelsTopLeft,
  Activity,
  Menu, 
  X,
  LogOut,
  User,
  Bell
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';

interface DashboardLayoutProps {
  children: ReactNode;
}

const navigationItems = [
  {
    name: '運用ダッシュボード',
    href: '/dashboard',
    icon: BarChart3,
  },
  {
    name: '取得履歴',
    href: '/dashboard/activity',
    icon: Activity,
  },
  {
    name: 'X API設定',
    href: '/dashboard/settings?tab=x-api',
    icon: Settings,
  },
  {
    name: 'Tracked Accounts',
    href: '/dashboard/settings?tab=accounts',
    icon: Radio,
  },
  {
    name: 'Widget設定',
    href: '/dashboard/settings?tab=widget',
    icon: PanelsTopLeft,
  },
  {
    name: 'APIキー管理',
    href: '/dashboard/api-keys',
    icon: Key,
  },
];

export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, tenant, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('ログアウトしました');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">
            XTimeline Console
          </h1>
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          {navigationItems.map((item) => {
            const itemUrl = new URL(item.href, window.location.origin);
            const currentTab = new URLSearchParams(location.search).get('tab');
            const targetTab = itemUrl.searchParams.get('tab');
            const isActive = targetTab
              ? location.pathname === itemUrl.pathname && currentTab === targetTab
              : location.pathname === itemUrl.pathname;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  nav-link mb-1
                  ${isActive ? 'nav-link-active' : ''}
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-primary-600" />
              </div>
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {tenant?.name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <Bell className="h-5 w-5" />
              </button>

              {/* User menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="flex items-center space-x-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:block">ログアウト</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
